<template>
	<v-dialog
		:value="value"
		content-class="haq-dialog db2-share-dialog"
		max-width="560px"
		persistent
		scrollable
		@input="$emit('input', $event)"
	>
		<v-card class="haq-dialog-card" v-if="dashboard">
			<v-card-title class="haq-dialog-card-header">
				<h5>Share {{ dashboard.name || 'dashboard' }}</h5>
				<v-spacer />
				<v-icon class="close-icon" @click="close">mdi-close</v-icon>
			</v-card-title>
			<v-card-text>
				<v-switch
					class="db2-share-toggle"
					v-model="shareProject"
					:true-value="true"
					:false-value="false"
					label="Everyone with project access"
					hide-details
					dense
					color="primary"
					:disabled="saving || loading"
				/>
				<div class="db2-share-field" :class="{ 'is-locked': listsLocked }">
					<HaqAutocomplete
						v-model="sharedUserIds"
						:items="userItems"
						item-text="label"
						item-value="objectId"
						label="Users"
						multiple
						hide-selected
						hide-details
						:loading="loading"
						:disabled="listsLocked"
						:menu-props="chipMenuProps"
						:filter="itemFilter"
					>
						<template v-slot:selection><span></span></template>
					</HaqAutocomplete>
					<div class="db2-share-chips" v-if="sharedUserIds.length">
						<v-chip
							v-for="id in sharedUserIds"
							:key="'u-'+id"
							:close="!listsLocked"
							class="db2-share-chip"
							@click:close="removeId('sharedUserIds', id)"
						>{{ itemLabel(id, userItems) }}</v-chip>
					</div>
				</div>
				<div class="db2-share-field" :class="{ 'is-locked': listsLocked }">
					<HaqAutocomplete
						v-model="sharedGroupIds"
						:items="groupItems"
						item-text="label"
						item-value="objectId"
						label="Groups"
						multiple
						hide-selected
						hide-details
						:loading="loading"
						:disabled="listsLocked"
						:menu-props="chipMenuProps"
						:filter="itemFilter"
					>
						<template v-slot:selection><span></span></template>
					</HaqAutocomplete>
					<div class="db2-share-chips" v-if="sharedGroupIds.length">
						<v-chip
							v-for="id in sharedGroupIds"
							:key="'g-'+id"
							:close="!listsLocked"
							class="db2-share-chip"
							@click:close="removeId('sharedGroupIds', id)"
						>{{ itemLabel(id, groupItems) }}</v-chip>
					</div>
				</div>
				<div class="db2-share-field" :class="{ 'is-locked': listsLocked }">
					<HaqAutocomplete
						v-model="sharedSiteIds"
						:items="siteItems"
						item-text="label"
						item-value="objectId"
						label="Sites"
						multiple
						hide-selected
						hide-details
						:loading="loading"
						:disabled="listsLocked"
						:menu-props="chipMenuProps"
						:filter="itemFilter"
					>
						<template v-slot:selection><span></span></template>
					</HaqAutocomplete>
					<div class="db2-share-chips" v-if="sharedSiteIds.length">
						<v-chip
							v-for="id in sharedSiteIds"
							:key="'s-'+id"
							:close="!listsLocked"
							class="db2-share-chip"
							@click:close="removeId('sharedSiteIds', id)"
						>{{ itemLabel(id, siteItems) }}</v-chip>
					</div>
				</div>
			</v-card-text>
			<v-card-actions>
				<v-spacer />
				<v-btn class="haq-btn haq-btn-secondary" :disabled="saving" @click="close">Cancel</v-btn>
				<v-btn class="haq-btn haq-btn-primary" :disabled="saving || loading || !dashboardId" @click="save">{{ saving ? 'Saving…' : 'Save' }}</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script>
import { mapState } from 'vuex';
import HaqAutocomplete from '@/components/form/HaqAutocomplete.vue';
import { HTTPClient } from '../../../assets/js/http';

const client = new HTTPClient();

/**
 * Object id from a Parse object, pointer JSON, or string.
 * @param {*} item
 * @returns {string|null}
 */
function objectIdOf(item) {
	if (!item) return null;
	if (typeof item === 'string') return item;
	return item.objectId || item.id || null;
}

/**
 * Ids from a share pointer list.
 * @param {*} raw
 * @returns {string[]}
 */
function idsFromList(raw) {
	const arr = Array.isArray(raw) ? raw : [];
	const ids = [];
	const seen = {};
	for (let i = 0; i < arr.length; i++) {
		const id = objectIdOf(arr[i]);
		if (!id || seen[id]) continue;
		seen[id] = true;
		ids.push(id);
	}
	return ids;
}

/**
 * Display name for a user row.
 * @param {object} user
 * @returns {string}
 */
function userLabel(user) {
	if (!user || typeof user === 'string') return user || '';
	const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
	const email = user.username || user.email || '';
	if (name && email) return name + ' (' + email + ')';
	return name || email || objectIdOf(user) || '';
}

/**
 * Display name for a group or site row.
 * @param {object} row
 * @returns {string}
 */
function namedLabel(row) {
	if (!row || typeof row === 'string') return row || '';
	return row.name || objectIdOf(row) || '';
}

/**
 * Share modal: project flag plus user / group / site chip autocompletes.
 */
export default {
	name: 'DashboardShareDialog',
	components: { HaqAutocomplete },
	props: {
		value: { type: Boolean, default: false },
		dashboard: { type: Object, default: null },
		projectId: { type: String, default: null }
	},
	data() {
		return {
			shareProject: false,
			sharedUserIds: [],
			sharedGroupIds: [],
			sharedSiteIds: [],
			users: [],
			groups: [],
			loading: false,
			saving: false,
			loadedProjectId: null,
			chipMenuProps: {
				bottom: true,
				offsetY: true,
				closeOnClick: true,
				closeOnContentClick: false,
				auto: true
			}
		};
	},
	computed: {
		...mapState({
			sites: state => state.sites
		}),
		/**
		 * Dashboard objectId.
		 * @returns {string|null}
		 */
		dashboardId() {
			return this.dashboard && this.dashboard.objectId ? this.dashboard.objectId : null;
		},
		/**
		 * True when listed users / groups / sites cannot be edited.
		 * @returns {boolean}
		 */
		listsLocked() {
			return this.shareProject === true || this.saving;
		},
		/**
		 * User autocomplete rows.
		 * @returns {Array}
		 */
		userItems() {
			return this.mergeItems(this.users.map(function(u) {
				return { objectId: objectIdOf(u), label: userLabel(u) };
			}), this.sharedUserIds, this.dashboard && this.dashboard.sharedUsers, userLabel);
		},
		/**
		 * Group autocomplete rows.
		 * @returns {Array}
		 */
		groupItems() {
			return this.mergeItems(this.groups.map(function(g) {
				return { objectId: objectIdOf(g), label: namedLabel(g) };
			}), this.sharedGroupIds, this.dashboard && this.dashboard.sharedGroups, namedLabel);
		},
		/**
		 * Site autocomplete rows for this project.
		 * @returns {Array}
		 */
		siteItems() {
			const pid = this.projectId;
			const rows = (this.sites || []).filter(function(s) {
				if (!s || s.hidden === true) return false;
				const sp = s.project;
				const spId = sp && (sp.objectId || sp.id || sp);
				return !pid || !spId || spId === pid;
			});
			return this.mergeItems(rows.map(function(s) {
				return { objectId: objectIdOf(s), label: namedLabel(s) };
			}), this.sharedSiteIds, this.dashboard && this.dashboard.sharedSites, namedLabel);
		}
	},
	watch: {
		value(open) {
			if (open) this.openForm();
		}
	},
	methods: {
		/**
		 * Copy share fields from the dashboard and load catalogs.
		 */
		async openForm() {
			const d = this.dashboard || {};
			this.shareProject = d.shareProject === true;
			this.sharedUserIds = idsFromList(d.sharedUsers);
			this.sharedGroupIds = idsFromList(d.sharedGroups);
			this.sharedSiteIds = idsFromList(d.sharedSites);
			await this.loadCatalogs();
		},
		/**
		 * Load project users and groups once per project.
		 */
		async loadCatalogs() {
			const projectId = this.projectId;
			if (!projectId || this.loadedProjectId === projectId) return;
			this.loading = true;
			try {
				const [usersRes, groupsRes] = await Promise.all([
					client.robustQuery('/admins/projects/' + projectId + '/users'),
					client.robustQuery('/admins/projects/' + projectId + '/groups')
				]);
				this.users = (usersRes && usersRes.users) || [];
				this.groups = (groupsRes && groupsRes.groups) || [];
				this.loadedProjectId = projectId;
			} catch (error) {
				console.error(error);
				window.M && window.M.toast && window.M.toast({ html: 'Failed to load share options' });
			} finally {
				this.loading = false;
			}
		},
		/**
		 * Catalog rows plus any selected ids that are missing.
		 * @param {Array} items
		 * @param {string[]} ids
		 * @param {Array} fallbackRows
		 * @param {Function} labelFn
		 * @returns {Array}
		 */
		mergeItems(items, ids, fallbackRows, labelFn) {
			const map = {};
			const list = Array.isArray(items) ? items : [];
			for (let i = 0; i < list.length; i++) {
				const row = list[i];
				if (row && row.objectId) map[row.objectId] = row;
			}
			const extra = Array.isArray(fallbackRows) ? fallbackRows : [];
			for (let i = 0; i < extra.length; i++) {
				const id = objectIdOf(extra[i]);
				if (id && !map[id]) map[id] = { objectId: id, label: labelFn(extra[i]) || id };
			}
			const selected = Array.isArray(ids) ? ids : [];
			for (let i = 0; i < selected.length; i++) {
				const id = selected[i];
				if (id && !map[id]) map[id] = { objectId: id, label: id };
			}
			return Object.keys(map).map(function(k) { return map[k]; });
		},
		/**
		 * Object id from an autocomplete selection.
		 * @param {*} item
		 * @returns {string|null}
		 */
		itemId(item) {
			return objectIdOf(item);
		},
		/**
		 * Chip text for a selection (id or item).
		 * @param {*} item
		 * @param {Array} items
		 * @returns {string}
		 */
		itemLabel(item, items) {
			if (item && item.label) return item.label;
			const id = objectIdOf(item);
			const list = Array.isArray(items) ? items : [];
			for (let i = 0; i < list.length; i++) {
				if (list[i].objectId === id) return list[i].label;
			}
			return id || '';
		},
		/**
		 * Case-insensitive label filter for chip autocompletes.
		 * @param {object} item
		 * @param {string} query
		 * @param {string} itemText
		 * @returns {boolean}
		 */
		itemFilter(item, query, itemText) {
			const q = (query || '').toString().toLowerCase();
			if (!q) return true;
			const text = (itemText || (item && item.label) || '').toString().toLowerCase();
			return text.indexOf(q) >= 0;
		},
		/**
		 * Drop one id from a selected-id array.
		 * @param {string} field
		 * @param {string|null} id
		 */
		removeId(field, id) {
			if (!id || this.listsLocked) return;
			this[field] = (this[field] || []).filter(function(x) { return x !== id; });
		},
		/**
		 * Close without saving.
		 */
		close() {
			if (this.saving) return;
			this.$emit('input', false);
		},
		/**
		 * Persist share fields.
		 */
		async save() {
			if (!this.dashboardId || this.saving) return;
			this.saving = true;
			try {
				const updateData = {
					shareProject: this.shareProject === true,
					sharedUsers: this.sharedUserIds.slice(),
					sharedGroups: this.sharedGroupIds.slice(),
					sharedSites: this.sharedSiteIds.slice()
				};
				const result = await this.$store.dispatch('updateDashboard', {
					dashboardId: this.dashboardId,
					updateData
				});
				const row = (result && result.results) || {};
				this.$store.commit('patchDashboardInList', {
					dashboardId: this.dashboardId,
					updateData: row
				});
				this.$emit('saved', row);
				this.$emit('input', false);
				window.M && window.M.toast && window.M.toast({ html: 'Sharing updated' });
			} catch (error) {
				console.error(error);
				window.M && window.M.toast && window.M.toast({ html: 'Failed to update sharing' });
			} finally {
				this.saving = false;
			}
		}
	}
};
</script>
